import Link from "next/link";

import { routes } from "src/shared/routes";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "How long does the Give & Go Monthly Pass subscription run?",
    answer:
      "The Give & Go Monthly Pass runs through the 2025-26 regular season. The final payment for the season will be processed on March 26th for games in April.",
  },
  {
    question: "How do I claim my tickets?",
    answer:
      "Tickets are assigned for each game on your schedule the day of your game.",
  },
  {
    question: "How are tickets sent to me?",
    answer: (
      <>
        You will receive a text when tickets are delivered to your Wizards
        account 3 hours prior to game start. Login to access and manage your
        tickets here{" "}
        <Link href={"https://am.ticketmaster.com/monumental/"} target="_blank">
          https://am.ticketmaster.com/monumental/
        </Link>
        .
      </>
    ),
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer:
      "Washington Wizards Give and Go tickets, as well as seat upgrades and guest passes, are not eligible for resale or transfer. Abusing these guidelines can result in your Washington Wizards Give and Go Pass being revoked.",
  },
  {
    question: "Can my seats be changed?",
    answer:
      "Once tickets have been sent to your account, they cannot be changed.",
  },
  {
    question: "Can I link tickets with other pass holders?",
    answer: (
      <>
        You can absolutely create a group to link with other pass holders!
        Navigate to the{" "}
        <Link
          href={{
            pathname: routes.my_group,
            query: { teamSchema: "washingtonwizards_pass" },
          }}
        >
          My Group
        </Link>{" "}
        tab in your menu and copy the invite. This will ensure you are seated
        next to or close enough to hand a drink to one another for the game. You
        will need to ensure your group is created and all members have joined at
        least 4 hours prior to game start in order to be seated with one
        another.
      </>
    ),
  },
  {
    question: "Can I bring a friend to a game?",
    answer:
      "Guest tickets will be available for select games. If available, you should see them available as an add-on.",
  },
  {
    question: "Can I receive a refund if I cannot attend a game?",
    answer: "We do not issue any refunds for games you are not able to attend.",
  },
  {
    question: "How can I get a refund and cancel my pass?",
    answer: (
      <>
        An active purchase for the month is not refundable. To cancel your pass
        for the upcoming month opt-out through the{" "}
        <Link
          href={{
            pathname: routes.manage_subscription,
            query: { teamSchema: "washingtonwizards_pass" },
          }}
        >
          Manage Subscription
        </Link>{" "}
        tab.
      </>
    ),
  },
  {
    question:
      "How can I change my payment method or update my expiration date?",
    answer: (
      <>
        To update your payment method, simply navigate to the{" "}
        <Link
          href={{
            pathname: routes.manage_subscription,
            query: { teamSchema: "washingtonwizards_pass" },
          }}
        >
          Manage Subscription
        </Link>{" "}
        menu on the right side of your screen. You will see a section to update
        your payment method where you can update new information.
      </>
    ),
  },
  {
    question: "What is the Give & Go payment schedule?",
    answer:
      "$69 will be charged on the 26th of the month prior. For example, if you sign up for a subscription before the start of the 25-26 season and opt in every month, you will be charged $69 at time of purchase, $69 on November 26, $69 on December 26, $69 on January 26, $69 on February 26, and $69 on March 26.",
  },
  {
    question: "Can I pause my subscription?",
    answer: (
      <>
        Yes, once you navigate to the{" "}
        <Link
          href={{
            pathname: routes.manage_subscription,
            query: { teamSchema: "washingtonwizards_pass" },
          }}
        >
          Manage Subscription
        </Link>{" "}
        tab in your account you can elect to pause your subscription for that
        month during the opt out window from the 15th to the 26th. You will
        receive a text message when this period is open.
      </>
    ),
  },
  {
    question: "Can I cancel my subscription?",
    answer: (
      <>
        Simply navigate to the{" "}
        <Link
          href={{
            pathname: routes.manage_subscription,
            query: { teamSchema: "washingtonwizards_pass" },
          }}
        >
          Manage Subscription
        </Link>{" "}
        tab to cancel your subscription. You can only do so during the monthly
        opt out period from the 15th to the 26th of the month.
      </>
    ),
  },
];

export const TERMS_AND_CONDITION: TTeamConfig["LANGUAGES"]["TERMS_AND_CONDITION"] =
  () => (
    <>
      By purchasing you agree to receive SMS alerts regarding ticket delivery
      and agree to the{" "}
      <Link target={"_blank"} href="https://www.attend.tech/terms-of-use">
        Terms of Use
      </Link>{" "}
      and{" "}
      <Link target={"_blank"} href="https://www.attend.tech/privacy-policy">
        Privacy Policy
      </Link>
    </>
  );

export const PASS_PURCHASE_SUCCESS_MESSAGE: TTeamConfig["LANGUAGES"]["PASS_PURCHASE_SUCCESS_MESSAGE"] =
  {
    TITLE: () => <>You&apos;e all set to experience the action!</>,
    DESCRIPTION: () => {
      const router = useRouterExt();
      return (
        <>
          Invite your friends! Create groups{" "}
          <b>
            <u>
              <span
                onClick={() => router.pushSchema(routes.my_group)}
                style={{ cursor: "pointer" }}
              >
                here
              </span>
            </u>
          </b>
          .
        </>
      );
    },
  };
